import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDemandStatusDto,
  CreateDemandStatusResponseDto,
  DemandStatusDto,
  GetDemandStatusesResponseDto,
  ReorderDemandStatusesDto,
  ReorderDemandStatusesResponseDto,
  UpdateDemandStatusDto,
  UpdateDemandStatusResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { demandStatusesQueryKeys } from '@/features/idea-management/queryKeys/demandStatuses';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

const stripHash = (color: string): string => color.replace(/^#/, '');
const withHash = (color: string): string => (color.startsWith('#') ? color : `#${color}`);
const normalizeStatus = (status: DemandStatusDto): DemandStatusDto => ({
  ...status,
  color: stripHash(status.color),
});

export const useDemandStatusesApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/demand-statuses`;

  const getDemandStatuses = async (): Promise<DemandStatusDto[]> => {
    const { data } = await httpClient.get<GetDemandStatusesResponseDto>(baseUrl);
    return data.data.map(normalizeStatus);
  };

  const createDemandStatus = async (dto: CreateDemandStatusDto): Promise<DemandStatusDto> => {
    const { data } = await httpClient.post<CreateDemandStatusResponseDto>(baseUrl, {
      ...dto,
      color: withHash(dto.color),
    });
    return normalizeStatus(data.data);
  };

  const updateDemandStatus = async (
    id: number,
    dto: UpdateDemandStatusDto,
  ): Promise<DemandStatusDto> => {
    const { data } = await httpClient.put<UpdateDemandStatusResponseDto>(`${baseUrl}/${id}`, {
      ...dto,
      ...(dto.color !== undefined ? { color: withHash(dto.color) } : {}),
    });
    return normalizeStatus(data.data);
  };

  const reorderDemandStatuses = async (
    dto: ReorderDemandStatusesDto,
  ): Promise<DemandStatusDto[]> => {
    const { data } = await httpClient.put<ReorderDemandStatusesResponseDto>(
      `${baseUrl}/reorder`,
      dto,
    );
    return data.data.map(normalizeStatus);
  };

  const deleteDemandStatus = async (id: number): Promise<void> => {
    await httpClient.delete(`${baseUrl}/${id}`);
  };

  const useGetAll = () =>
    useQuery({
      queryKey: demandStatusesQueryKeys.lists(),
      queryFn: getDemandStatuses,
    });

  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: demandStatusesQueryKeys.all() });

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createDemandStatus,
      onSuccess: invalidate,
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: ({ id, dto }: { id: number; dto: UpdateDemandStatusDto }) =>
        updateDemandStatus(id, dto),
      onSuccess: invalidate,
    });

  const useReorder = () =>
    useErrorHandlingMutation({
      mutationFn: reorderDemandStatuses,
      onSuccess: invalidate,
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemandStatus,
      onSuccess: invalidate,
    });

  return {
    getDemandStatuses,
    useGetAll,
    useCreate,
    useUpdate,
    useReorder,
    useDelete,
  };
};
